import { useCallback, useState } from 'react';

import { useQueryState } from 'hooks/useQueryState';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/storageUtils';

import { ApprovalsTab } from './ApprovalsTabs';

export const TAB_STORAGE_KEY = 'approvals.selectedTab';

/**
 * Not `hooks/useLocalStorageItem`, which owns a piece of state and writes it
 * back on every change: the open tab is already owned by the query string, and
 * a second owner would mean two answers to "which tab is open". These read and
 * write the same key through the shared helpers without holding the value.
 *
 * The try/catch is still needed on top of them. `storageUtils` guards on
 * `window.localStorage` being *present*, but a browser with storage disabled
 * throws on the property access itself, and Safari's private mode throws on
 * write — both before the helper's own guard can run. Losing the remembered
 * tab is not a reason to fail the page or the click.
 */
const readRemembered = (): string | null => {
  try {
    return getLocalStorageItem(TAB_STORAGE_KEY) as string | null;
  } catch {
    return null;
  }
};

const remember = (id: string) => {
  try {
    setLocalStorageItem(TAB_STORAGE_KEY, id);
  } catch {
    // Not remembering the tab is not a reason to fail the interaction.
  }
};

/**
 * Which approvals tab is open, and how that survives leaving the page.
 *
 * Three sources, in descending precedence:
 *
 *   `?tab=` in the URL — an explicit request, so it wins. It is also what makes
 *     a tab linkable and survives a refresh; `opening_approvals` already takes
 *     a tab in its query string, and the monolith deep-links into it.
 *   localStorage — the tab this admin last chose, which is the "remembered"
 *     part of the requirement: unlike the query string it survives navigating
 *     away to settings and coming back.
 *   `tabs[0]` — the default, Approver rules.
 *
 * A remembered or linked id that names no current tab falls through to the
 * default rather than selecting nothing: tabs come and go as the sibling
 * tickets land, and a stale id in someone's browser must not produce a page
 * with no tab selected and no panel rendered.
 */
export function useApprovalsTab(
  tabs: ApprovalsTab[],
): [string, (id: string) => void] {
  const fallback = tabs[0].id;
  // Read once. Re-reading each render would let a write further down flip the
  // fallback mid-session, which is invisible while a `?tab=` is set and
  // confusing on the render where it is cleared.
  const [remembered] = useState(readRemembered);
  const known = (id: string | null) =>
    tabs.some(tab => tab.id === id) ? (id as string) : fallback;

  const [queryTab, setQueryTab] = useQueryState('tab', known(remembered));

  const select = useCallback(
    (id: string) => {
      remember(id);
      setQueryTab(id);
    },
    [setQueryTab],
  );

  return [known(queryTab), select];
}
